import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import VersionsView from './versions-view';
import useURLSearchQuery from '../../../../hooks/use-url-for-search-query';
import { useEntityVersions } from './use-entity-versions';
import { EntityType, EntityVersion, VersionsFilters } from '../types';

interface VersionsSearch {
  [key: string]: string | string[];
  entityType: string;
  id: string;
  all: string;
}

const defaultSearch: VersionsSearch = {
  entityType: '',
  id: '',
  all: '',
};

const VersionsContainer: React.FC = () => {
  const navigate = useNavigate();
  const [queryValues, updateURLQuery] = useURLSearchQuery<VersionsSearch>(defaultSearch);

  const { entityType, id, all } = queryValues;

  const filters: VersionsFilters = useMemo(() => ({
    entityType: (entityType as EntityType) || undefined,
    id: id || undefined,
    all: all === 'true',
  }), [entityType, id, all]);

  const {
    versions,
    rawVersions,
    isLoading,
    error,
    hasMore,
    loadMore,
  } = useEntityVersions(filters);

  const handleFilterChange = (name: string, value: unknown) => {
    if (typeof value === 'boolean') {
      updateURLQuery({ [name]: value ? 'true' : '' });
    } else {
      updateURLQuery({ [name]: String(value ?? '') });
    }
  };

  const handleVersionClick = useCallback((version: EntityVersion) => {
    if (entityType && id) {
      navigate(`compare/${entityType}/${id}?updateId=${version.updateId}&all=${all === 'true'}`);
    }
  }, [navigate, entityType, id, all]);

  return (
    <VersionsView
      versions={versions}
      rawVersions={rawVersions}
      isLoading={isLoading}
      error={error}
      hasMore={hasMore}
      loadMore={loadMore}
      filters={{
        entityType: entityType as EntityType | '',
        id,
        all: all === 'true',
      }}
      onFilterChange={handleFilterChange}
      onVersionClick={handleVersionClick}
    />
  );
};

export default VersionsContainer;
